import { differenceInMonths } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  useQueryGetSnapshotLatestExperianData,
} from 'services/monitor/queries';
import { useSSEEvents, EventsAvailable } from '@capitalise/common';

export const eventStatus = Object.freeze({
  notStarted: 'notStarted',
  generating: 'generating',
  error: 'error',
  finished: 'finished',
});

export const transformEventData = (jsonData) => {
  try {
    const { event, data: eventData } = jsonData;
    if (event === EventsAvailable.relatedCompaniesUpdate) {
      return { finished: false, percentage: 0, error: undefined, syncRelatedCompanies: true };
    }
    if (event !== EventsAvailable.snapshotGeneration) {
      return null;
    }
    const { finished, percentage, error } = eventData;
    return {
      finished: finished || percentage === 100,
      percentage,
      error,
      syncRelatedCompanies: false,
    };
  } catch (error) {
    return { finished: false, percentage: 0, error: error.message, syncRelatedCompanies: false };
  }
};

export const useSnapshotGeneration = ({
  entityId,
  checkFn = undefined,
  forceRetryTimeout = false,
}) => {
  const {
    data,
    isLoading: isLoadingLatest,
    refetch,
  } = useQueryGetSnapshotLatestExperianData(entityId);
  const queryClient = useQueryClient();
  const experianData = data?.experianData;

  const checkResult = checkFn?.(experianData);
  const hasCheckFnFalse = checkFn && !checkResult;
  const hasData = checkFn ? !!checkResult : Object.keys(experianData ?? {}).length > 0;

  const progress = useRef(0);
  const [status, setStatus] = useState(eventStatus.notStarted);
  const event = useSSEEvents();
  const isOlderThanOneMonth = experianData?.generatedAt
    ? differenceInMonths(new Date(), experianData?.generatedAt) > 1
    : true;
  const isLoading =
    status === !!entityId ? eventStatus.generating || status === eventStatus.notStarted : false;

  useEffect(() => {
    if (hasData && !isLoadingLatest) {
      setStatus(eventStatus.finished);
    }
    if (
      entityId &&
      status === eventStatus.notStarted &&
      !isLoadingLatest &&
      (isOlderThanOneMonth || hasCheckFnFalse)
    ) {
      setStatus(eventStatus.generating);
    }
  }, [status, event, entityId, experianData, isLoadingLatest]);

  useEffect(() => {
    if (event) {
      const update = transformEventData(event);
      if (update) {
        progress.current = update.percentage;
        if (update.finished) {
          setStatus(update.error ? eventStatus.error : eventStatus.finished);
          if (!update.error) {
            queryClient.invalidateQueries('snapshot-latest');
            refetch();
          }
        }
      }
    }
  }, [event]);

  useEffect(() => {
    if (!!forceRetryTimeout && isLoading) {
      setTimeout(() => {
        refetch();
      }, forceRetryTimeout);
    }
  }, []);

  return {
    progress: progress.current,
    status: status,
    isLoading: isLoading,
    data: data?.experianData,
    result: data?.result,
    refetch,
  };
};
